import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import InputAdornment from '@mui/material/InputAdornment';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { Link } from 'react-router-dom';
import APP_CONFIG from '@/app.config';
import UserFormDialog from '../components/UserFormDialog';
import UsersTable from '../components/UsersTable';
import {
  createAdminUser,
  listAdminUsers,
  sendAdminUserPasswordReset,
  setAdminUserActiveState,
  setAdminUserArchivedState,
  updateAdminUser,
} from '../services/adminUsersService';
import type {
  AdminUserFormValues,
  AdminUserSortDirection,
  AdminUserSortField,
  AdminUserStatusFilter,
  AdminUserSummary,
} from '../types/adminUsers';

type DialogMode = 'create' | 'edit';

type ConfirmAction = {
  type: 'active' | 'archive';
  user: AdminUserSummary;
} | null;

const QUERY_KEY = ['admin-users'];

const normalizeText = (value: string): string => value.trim().toLowerCase();

const getLastActivityTime = (user: AdminUserSummary): number => {
  return (
    user.lastLoginAt?.getTime()
    || user.lastPasswordResetEmailAt?.getTime()
    || user.updatedAt?.getTime()
    || user.lastEmailSentAt?.getTime()
    || 0
  );
};

export default function UsersAdminPage() {
  const queryClient = useQueryClient();
  const usersQuery = useQuery({
    queryKey: QUERY_KEY,
    queryFn: listAdminUsers,
  });

  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<'all' | AdminUserSummary['role']>('all');
  const [statusFilter, setStatusFilter] = useState<AdminUserStatusFilter>('all');
  const [includeArchived, setIncludeArchived] = useState(false);
  const [sortBy, setSortBy] = useState<AdminUserSortField>('createdAt');
  const [sortDirection, setSortDirection] = useState<AdminUserSortDirection>('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [dialogMode, setDialogMode] = useState<DialogMode>('create');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AdminUserSummary | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [busyAction, setBusyAction] = useState<{ uid: string; action: string } | null>(null);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);

  const users = usersQuery.data || [];

  const filteredUsers = useMemo(() => {
    const query = normalizeText(search);
    const result = users.filter((user) => {
      if (!includeArchived && user.deleted) {
        return false;
      }

      if (roleFilter !== 'all' && user.role !== roleFilter) {
        return false;
      }

      if (statusFilter === 'active' && (!user.active || user.deleted)) {
        return false;
      }

      if (statusFilter === 'inactive' && user.active && !user.deleted) {
        return false;
      }

      if (!query) {
        return true;
      }

      const haystack = [user.fullName, user.email, user.phone, user.role, user.internalNotes]
        .join(' ')
        .toLowerCase();

      return haystack.includes(query);
    });

    result.sort((left, right) => {
      const direction = sortDirection === 'asc' ? 1 : -1;

      switch (sortBy) {
        case 'fullName':
          return left.fullName.localeCompare(right.fullName, 'fr') * direction;
        case 'email':
          return left.email.localeCompare(right.email, 'fr') * direction;
        case 'role':
          return left.role.localeCompare(right.role, 'fr') * direction;
        case 'status':
          return `${left.deleted ? 'archived' : left.active ? 'active' : 'inactive'}`
            .localeCompare(`${right.deleted ? 'archived' : right.active ? 'active' : 'inactive'}`, 'fr') * direction;
        case 'lastActivityAt':
          return (getLastActivityTime(left) - getLastActivityTime(right)) * direction;
        case 'createdAt':
        default:
          return ((left.createdAt?.getTime() ?? 0) - (right.createdAt?.getTime() ?? 0)) * direction;
      }
    });

    return result;
  }, [includeArchived, roleFilter, search, sortBy, sortDirection, statusFilter, users]);

  const paginatedUsers = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredUsers.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredUsers, page, rowsPerPage]);

  const refreshUsers = async () => {
    await queryClient.invalidateQueries({ queryKey: QUERY_KEY });
  };

  const handleSortChange = (field: AdminUserSortField) => {
    setPage(0);
    if (sortBy === field) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setSortBy(field);
    setSortDirection(field === 'createdAt' || field === 'lastActivityAt' ? 'desc' : 'asc');
  };

  const openCreateDialog = () => {
    setDialogMode('create');
    setSelectedUser(null);
    setDialogOpen(true);
  };

  const openEditDialog = (user: AdminUserSummary) => {
    setDialogMode('edit');
    setSelectedUser(user);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (submitting) {
      return;
    }

    setDialogOpen(false);
    setSelectedUser(null);
  };

  const handleSubmitDialog = async (values: AdminUserFormValues) => {
    setSubmitting(true);
    try {
      if (dialogMode === 'create') {
        const result = await createAdminUser(values);
        toast.success(result.passwordResetEmailSent === false
          ? 'Utilisateur créé. Vérifiez l’envoi de l’email de réinitialisation.'
          : 'Utilisateur créé et email de réinitialisation envoyé.');

        if (result.passwordResetWarning) {
          toast.error(result.passwordResetWarning);
        }
      } else if (selectedUser) {
        await updateAdminUser({
          uid: selectedUser.uid,
          firstName: values.firstName,
          lastName: values.lastName,
          role: values.role,
          active: values.active,
          phone: values.phone,
          internalNotes: values.internalNotes,
        });
        toast.success('Utilisateur mis à jour.');
      }

      await refreshUsers();
      setDialogOpen(false);
      setSelectedUser(null);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Une erreur est survenue.';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handlePasswordReset = async (user: AdminUserSummary) => {
    setBusyAction({ uid: user.uid, action: 'reset' });
    try {
      await sendAdminUserPasswordReset({ uid: user.uid });
      toast.success(`Email de réinitialisation envoyé à ${user.email}.`);
      await refreshUsers();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Impossible d’envoyer le reset.';
      toast.error(message);
    } finally {
      setBusyAction(null);
    }
  };

  const handleConfirmActiveToggle = async () => {
    if (!confirmAction || confirmAction.type !== 'active') {
      return;
    }

    const user = confirmAction.user;
    setBusyAction({ uid: user.uid, action: 'active' });
    try {
      await setAdminUserActiveState({ uid: user.uid, active: !user.active });
      toast.success(!user.active ? 'Compte activé.' : 'Compte désactivé.');
      await refreshUsers();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Impossible de modifier le statut.';
      toast.error(message);
    } finally {
      setBusyAction(null);
      setConfirmAction(null);
    }
  };

  const handleConfirmArchiveToggle = async () => {
    if (!confirmAction || confirmAction.type !== 'archive') {
      return;
    }

    const user = confirmAction.user;
    setBusyAction({ uid: user.uid, action: 'archive' });
    try {
      await setAdminUserArchivedState({ uid: user.uid, deleted: !user.deleted });
      toast.success(user.deleted ? 'Compte restauré.' : 'Compte archivé.');
      await refreshUsers();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Impossible de modifier l’archivage.';
      toast.error(message);
    } finally {
      setBusyAction(null);
      setConfirmAction(null);
    }
  };

  const resetFilters = () => {
    setSearch('');
    setRoleFilter('all');
    setStatusFilter('all');
    setIncludeArchived(false);
    setPage(0);
  };

  const dialogTitle = confirmAction?.type === 'archive'
    ? confirmAction.user.deleted ? 'Restaurer ce compte ?' : 'Archiver ce compte ?'
    : confirmAction?.user.active ? 'Désactiver ce compte ?' : 'Activer ce compte ?';

  const dialogMessage = confirmAction?.type === 'archive'
    ? confirmAction.user.deleted
      ? 'Le compte sera à nouveau visible dans la liste principale et réactivable ensuite.'
      : 'Le compte sera masqué par défaut, conservé dans Firestore et désactivé côté Firebase Auth.'
    : confirmAction?.user.active
      ? 'L’utilisateur ne pourra plus utiliser l’application tant que le compte restera inactif.'
      : 'L’utilisateur pourra à nouveau se connecter et utiliser l’application.';

  return (
    <Box sx={{ 
      px: { xs: 2, md: 4 }, 
      py: { xs: 4, md: 6 }, 
      mt: { xs: 8, md: 12 },
      maxWidth: 1400, 
      mx: 'auto' 
    }}>
      <Stack spacing={3}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
          <Box>
            <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 700, letterSpacing: '0.18em' }}>
              Administration
            </Typography>
            <Typography variant="h3" sx={{ mt: 1 }}>
              Gestion des utilisateurs
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
              Recherchez, créez, modifiez, activez, archivez et réinitialisez les accès sans exposer la logique sensible côté client.
            </Typography>
          </Box>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignSelf={{ md: 'flex-start' }}>
            <Button component={Link} to="/admin" color="inherit" variant="outlined">
              Retour au dashboard
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => usersQuery.refetch()}
              disabled={usersQuery.isFetching}
            >
              Rafraîchir
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>
              Nouvel utilisateur
            </Button>
          </Stack>
        </Stack>

        <Paper elevation={0} sx={{ p: 2.5, borderRadius: 4, border: '1px solid', borderColor: 'divider' }}>
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2}>
            <TextField
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(0);
              }}
              placeholder="Rechercher par nom, email, téléphone ou note"
              fullWidth
              size="small"
              sx={{ flexGrow: { lg: 1 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ width: { xs: '100%', lg: 'auto' } }}>
              <FormControl size="small" sx={{ minWidth: { sm: 180 }, flexGrow: { xs: 1, sm: 0 } }}>
                <InputLabel id="users-role-filter-label">Rôle</InputLabel>
                <Select
                  labelId="users-role-filter-label"
                  label="Rôle"
                  value={roleFilter}
                  onChange={(event) => {
                    setRoleFilter(event.target.value as typeof roleFilter);
                    setPage(0);
                  }}
                >
                  <MenuItem value="all">Tous les rôles</MenuItem>
                  {APP_CONFIG.userRoles.map((role) => (
                    <MenuItem key={role} value={role}>
                      {role}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: { sm: 180 }, flexGrow: { xs: 1, sm: 0 } }}>
                <InputLabel id="users-status-filter-label">Statut</InputLabel>
                <Select
                  labelId="users-status-filter-label"
                  label="Statut"
                  value={statusFilter}
                  onChange={(event) => {
                    setStatusFilter(event.target.value as AdminUserStatusFilter);
                    setPage(0);
                  }}
                >
                  <MenuItem value="all">Tous statuts</MenuItem>
                  <MenuItem value="active">Actifs</MenuItem>
                  <MenuItem value="inactive">Inactifs</MenuItem>
                </Select>
              </FormControl>
            </Stack>

            <Stack 
              direction="row" 
              spacing={2} 
              alignItems="center" 
              justifyContent={{ xs: 'space-between', lg: 'flex-start' }}
              sx={{ minWidth: 'max-content' }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={includeArchived}
                    onChange={(event) => {
                      setIncludeArchived(event.target.checked);
                      setPage(0);
                    }}
                    size="small"
                  />
                }
                label={<Typography variant="body2">Inclure archivés</Typography>}
              />

              <Button 
                variant="text" 
                onClick={resetFilters} 
                size="small"
                sx={{ fontWeight: 600 }}
              >
                Réinitialiser
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {usersQuery.error ? (
          <Alert severity="error">
            {usersQuery.error instanceof Error ? usersQuery.error.message : 'Impossible de charger les utilisateurs.'}
          </Alert>
        ) : null}

        <Paper elevation={0} sx={{ p: 2, borderRadius: 4, border: '1px solid', borderColor: 'divider' }}>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1} sx={{ mb: 2 }}>
            <Typography variant="h6">
              {filteredUsers.length} utilisateur{filteredUsers.length > 1 ? 's' : ''} affiché{filteredUsers.length > 1 ? 's' : ''}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Seuls les administrateurs peuvent gérer tous les comptes. Les mutations sensibles passent par des Cloud Functions sécurisées.
            </Typography>
          </Stack>

          <UsersTable
            users={paginatedUsers}
            loading={usersQuery.isLoading || usersQuery.isFetching}
            page={page}
            rowsPerPage={rowsPerPage}
            totalCount={filteredUsers.length}
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSortChange={handleSortChange}
            onPageChange={(_event, nextPage) => setPage(nextPage)}
            onRowsPerPageChange={(nextRowsPerPage) => {
              setRowsPerPage(nextRowsPerPage);
              setPage(0);
            }}
            onEditUser={openEditDialog}
            onToggleActive={(user) => setConfirmAction({ type: 'active', user })}
            onPasswordReset={handlePasswordReset}
            onArchiveToggle={(user) => setConfirmAction({ type: 'archive', user })}
            busyAction={busyAction}
          />
        </Paper>
      </Stack>

      <UserFormDialog
        open={dialogOpen}
        mode={dialogMode}
        user={selectedUser}
        loading={submitting}
        onClose={closeDialog}
        onSubmit={handleSubmitDialog}
      />

      <Dialog open={Boolean(confirmAction)} onClose={busyAction ? undefined : () => setConfirmAction(null)} fullWidth maxWidth="sm">
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent>
          <Typography variant="body1">{dialogMessage}</Typography>
          {confirmAction ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Compte concerné: {confirmAction.user.fullName} ({confirmAction.user.email || 'email non renseigné'})
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmAction(null)} disabled={Boolean(busyAction)} color="inherit">
            Annuler
          </Button>
          <Button
            onClick={confirmAction?.type === 'archive' ? handleConfirmArchiveToggle : handleConfirmActiveToggle}
            disabled={Boolean(busyAction)}
            variant="contained"
            color={confirmAction?.type === 'archive' && !confirmAction?.user.deleted ? 'warning' : 'primary'}
          >
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}